import { ApolloError } from "@apollo/client";
import { StudioSpeaker, StudioVoiceModel } from "../../@types/data";
import { Query, Speaker } from "../../@types/generated/types";
import { catalogErrors } from "../../utils/catalogErrors";

export const speakersToApprovedVoicesFactory = (s: Speaker[]) => {
  return s.filter(speaker => !speaker.name);
};

// Puts STUDIO_SPEAKERS_CATALOG's result on the channel speakers. The labels read
// a speaker without `catalog` as not loaded yet, so a request that failed
// without data gives every speaker `catalog: null` instead.
export const speakersWithCatalogFactory = (
  speakers: Speaker[],
  catalogData: Pick<Query, "translatedVideo"> | undefined,
  catalogError: ApolloError | undefined
): StudioSpeaker[] => {
  const catalogSpeakers = catalogData?.translatedVideo?.channel.speakers;
  if (!catalogSpeakers) {
    if (!catalogError) return speakers;
    return speakers.map(speaker => ({ ...speaker, catalog: null }));
  }
  const errorsByPath = catalogErrors(catalogError);
  const catalogById = new Map(
    catalogSpeakers.map((speaker, i) => [
      speaker.id,
      {
        catalog: speaker.catalog,
        catalogError: errorsByPath[`translatedVideo.channel.speakers.${i}`]
      }
    ])
  );
  return speakers.map(speaker => ({
    ...speaker,
    ...catalogById.get(speaker.id)
  }));
};

// A voice whose models failed to load is left out, so they read as unknown.
export const voiceModelsFactory = (
  data: Pick<Query, "translatedVideo">
): Record<string, StudioVoiceModel[]> => {
  const models: Record<string, StudioVoiceModel[]> = {};
  for (const speaker of data.translatedVideo?.channel.speakers ?? []) {
    const voice = speaker.catalog?.voice;
    if (voice?.models) models[voice.id] = voice.models;
  }
  return models;
};

export const speakersToCharactersFactory = <T extends Speaker>(s: T[]) => {
  const speakers = s.filter(speaker => !!speaker.name);
  return speakers.sort((a, b) => {
    // speakers here will alwasy have a name
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return a.name!.localeCompare(b.name!);
  });
};
